import type { CoverageStatus } from '@/lib/api'
import type { ExtractedComplianceItem } from '@/types/extraction'

export type ProgressBucket = 'not_started' | 'in_progress' | 'in_review' | 'done' | 'no_action'

export interface EffectiveProgress {
  /** null = post-award: the row has no Progress at all (spec B4). */
  bucket: ProgressBucket | null
  /** True when the shown value came from coverage, not the stored status. */
  derived: boolean
}

export const PROGRESS_LABELS: Record<ProgressBucket, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  in_review: 'In review',
  done: 'Done',
  no_action: 'No action',
}

export const PROGRESS_FILTER_OPTIONS: Array<{ value: ProgressBucket; label: string }> = (
  ['not_started', 'in_progress', 'in_review', 'done', 'no_action'] as const
).map((value) => ({ value, label: PROGRESS_LABELS[value] }))

const COVERAGE_TO_BUCKET: Record<CoverageStatus, ProgressBucket> = {
  unmapped: 'not_started',
  mapped: 'in_progress',
  drafting: 'in_progress',
  in_review: 'in_review',
  addressed: 'done',
}

const STATUS_TO_BUCKET: Record<string, ProgressBucket> = {
  pending: 'not_started',
  in_progress: 'in_progress',
  completed: 'done',
  not_applicable: 'no_action',
}

/**
 * Only an explicit `statusSource: 'manual'` makes a narrative row manual. The
 * API always sends the field (the column defaults to 'derived'); rows without
 * it are freshly streamed extraction rows nobody has touched.
 *
 * A derived row with no coverage row yet (nothing mapped, or coverage still
 * loading) is unmapped — Not started — and stays derived. Its stored status is
 * ignored: a row handed back to derivation keeps whatever was last set by hand.
 */
export function effectiveProgress(
  item: ExtractedComplianceItem,
  coverageStatus: CoverageStatus | undefined
): EffectiveProgress {
  const expectation = item.responseExpectation ?? 'narrative'
  if (expectation === 'post_award') return { bucket: null, derived: false }
  if (expectation === 'narrative' && item.statusSource !== 'manual') {
    return { bucket: COVERAGE_TO_BUCKET[coverageStatus ?? 'unmapped'], derived: true }
  }
  return { bucket: STATUS_TO_BUCKET[item.status] ?? 'not_started', derived: false }
}
